<template>
  <div v-if="isLoading">
    <div class="">
      <div class="dots-loader">
      </div>
      <p class="absolute top-1/3 left-1/2 transform -translate-x-1/2">Loading, please wait ..</p>
      <div class="dots-loader"></div>
    </div>

  </div>

  <div v-else>
    <template v-if="authStore.maintenanceInfo.a === true">
      <UnderMaintenance/>
    </template>
    <template v-else>
      <component :is="layout"></component>
    </template>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from "vue";
import {useApiStore} from "@/stores/api";
import {useAuthStore} from "@/stores/auth";
import {useRoute, useRouter} from "vue-router";
import type {LoggedInUserData} from "@/types/LoggedInuser";
import UnderMaintenance from "@/components/layout/UnderMaintenance.vue";
import {User, type UserData} from "@/types/user";
import {MaintenanceInfo} from "@/types/MaintenanceInfo";
import type {QueryFilter} from "@/types/object_classes/QueryFilter";
import {Release, type ReleaseData} from "@/types/site_admin/releases";
import {UserSetting} from "@/types/userSettings";
import {useIdleTimeout} from "@/helpers/useIdleTimeout";

const ONECLICK_ENV = ref(import.meta.env.VITE_ONECLICK_ENV);

const authStore = useAuthStore();
const auth = useAuthStore();
const apiStore = useApiStore();
const api = useApiStore();
const isLoading = ref(true);
const router = useRouter();
const route = useRoute();
const user = ref<User[]>([]);

useIdleTimeout();


const layout = computed(() => {
  return route.meta.layout || "LayoutLoggedIn";
});

const routeName = computed(() => {
  return route.name;
});


// Public pages opened from an email (e.g. the unsubscribe link) must stay fully OUTSIDE the
// authenticated bootstrap: no session refresh, no /user call, no post-login redirects — regardless
// of whether the browser holds an active, stale, or no session. Otherwise a live session redirects
// to news/dashboard (releases block) and an expired one bounces to /login (interceptor logout).
const PUBLIC_UTILITY_ROUTES = ["unsubscribe"];
const onPublicUtilityRoute = computed(() =>
    PUBLIC_UTILITY_ROUTES.includes(routeName.value as string)
);

router.isReady().then(async () => {
  const onAuthHandoffRoute = ['callback', 'impersonate', 'impersonate-callback'].includes(route.name as string);
  const onImpersonationRoute = ['impersonate', 'impersonate-callback'].includes(route.name as string);
  await auth.initApp({skipRefresh: onAuthHandoffRoute || onPublicUtilityRoute.value, skipImpersonation: onImpersonationRoute});
  fetchData();
})

watch(() => authStore.token, (newToken, oldToken) => {
  if (!isLoading.value && newToken && !oldToken) {
    isLoading.value = true;
    fetchData();
  }
})

const fetchData = () => {
  const loadPromises: Promise<any>[] = [];
  loadPromises.push(api.getCustomAction('maintenances', null, 'isMaintenanceActive'));
  const shouldFetchUser = !onPublicUtilityRoute.value && (!!authStore.token || (
      routeName.value !== "callback" &&
      routeName.value !== "impersonate" &&
      routeName.value !== "impersonate-callback" &&
      routeName.value !== "forgot-password" &&
      routeName.value !== "reset-password" &&
      routeName.value !== "confirm-email" &&
      routeName.value !== "unsubscribe" &&
      routeName.value !== "tracking-shipment"
  ));
  if (shouldFetchUser) {
    loadPromises.push(apiStore.axiosAuth.get("/user"))
  }

  Promise.allSettled(loadPromises)
      .then(async (result) => {
        if (result[0].status !== "fulfilled") {
          console.log("failed reading maintenance data");
          // return;
        }
        authStore.maintenanceInfo = new MaintenanceInfo(result[0].value);

        if (!shouldFetchUser) {
          return;
        }

        if (result[1].status !== "fulfilled") {
          authStore.isLoggedIn = false
          if (![
            "login", "callback", "impersonate", "impersonate-callback",
            "create-password", "reset-password",
            "confirm-email", "unsubscribe",
            "tracking-shipment"
          ].includes(route.name as string)) {
            await router.push({name: "login"});
          }
        }

        if (result[1].status === "fulfilled") {
          authStore.isLoggedIn = true;
          authStore.setUser(result[1].value.data as LoggedInUserData)

          await api.getItems<Release, ReleaseData>(
              'releases',
              Release,
              undefined,
              {releases: ['client_release_date', 'self_service_release_date']},
              undefined,
              undefined,
              ['-release_date', '-id'],
              undefined,
              {ignore_errors: true}
          )
              .then(async (releases) => {
                let latestReleaseDate;
                if (releases[0]?.client_release_date !== null) {
                  latestReleaseDate = releases[0]?.client_release_date;
                } else {
                  latestReleaseDate = releases[0]?.self_service_release_date;
                }

                if(auth.user.au === null && (auth.user.lastReleaseRead === null || (new Date(auth.user.lastReleaseRead.data.release_date)).getTime() - latestReleaseDate.getTime() < 0))
                {
                  const last_release_setting = new UserSetting();
                  last_release_setting.name = 'last_release_read';
                  last_release_setting.data = {
                    release_id: releases[0].id,
                    release_date: latestReleaseDate
                  };

                  if(auth.user.lastReleaseRead === null)
                  {
                    api.createItem("user-settings", last_release_setting, UserSetting);
                  }
                  else
                  {
                    last_release_setting.id = auth.user.lastReleaseRead.id;
                    api.saveItem("user-settings", auth.user.lastReleaseRead.id, last_release_setting, UserSetting);
                  }

                  if (releases[0].client_release_date !== null) {
                    await router.push({ name: 'news'})
                  } else {
                    await router.push({ name: 'self-service-news'})
                  }
                  return;
                }
              })
              .catch((error) => {
                console.log("failed reading releases data");
              })

          if (routeName.value === "login") {
            await router.push({name: "home"});
          }
        }
      })
      .finally(() => {
        isLoading.value = false;
      })
}


</script>
<style scoped>
</style>
